// ===========================================
// BUSTED! BLACKJACK - GAME STATE STORE
// ===========================================
// Zustand store for managing blackjack game state
// Derived from processed game events
// ===========================================

import { create } from 'zustand';
import {
  BlackjackEvent,
  PlayerGameStatus,
  RoundState,
  PlayerGameState,
  DealerState,
  HandState,
  Card,
  createRoundState,
  createEmptyDealerState,
  createEmptyHandState,
  calculateHandScore,
  GamePhase,
  SeatControlState,
} from '@busted-blackjack/shared';

// ===========================================
// DETERMINISTIC VISUAL VALUE GENERATION
// ===========================================
// Generates consistent visual values for cards based on their identity.
// Uses a simple seeded random so all clients produce identical values
// for the same card (simulating server-authoritative visual state).

/**
 * Simple seeded random number generator (mulberry32)
 * Given the same seed, produces the same sequence of "random" numbers.
 */
function seededRandom(seed: number): () => number {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/**
 * Generate a numeric seed from card identity strings.
 * Combines seat, hand, card index, rank, and suit into a deterministic seed.
 */
function cardSeed(seatIndex: number, handIndex: number, cardIndex: number, rank: string, suit: string): number {
  const str = `${seatIndex}-${handIndex}-${cardIndex}-${rank}-${suit}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Enrich a card with deterministic visual values if not already present.
 * This simulates server-authoritative visual state for demo/testing.
 */
function enrichCardWithVisuals(
  card: { rank: string; suit: string; faceUp: boolean; visualRotation?: number; visualOffsetY?: number },
  seatIndex: number,
  handIndex: number,
  cardIndex: number
): Card {
  // If server already provided visual values, use them
  if (card.visualRotation !== undefined && card.visualOffsetY !== undefined) {
    return card as Card;
  }

  // Generate deterministic visual values based on card identity
  const seed = cardSeed(seatIndex, handIndex, cardIndex, card.rank, card.suit);
  const random = seededRandom(seed);

  // Same logic as Card component's local generation:
  // Y = 1-10, if Y === 1 then X = 25 (sloppy), else X = 5 (normal)
  const Y = Math.floor(random() * 10) + 1;
  const Z = 5;
  const X = Y === 1 ? Z * Z : Z;

  const direction = random() < 0.5 ? 1 : -1;
  const visualRotation = direction * random() * X;
  const visualOffsetY = (random() * 2 - 1) * X;

  return {
    ...card,
    suit: card.suit as Card['suit'],
    rank: card.rank as Card['rank'],
    visualRotation,
    visualOffsetY,
  };
}

// ===========================================
// TYPES
// ===========================================

export interface DealerPersona {
  name: string;
  lastName: string;
  avatarUrl: string;
  gender: 'male' | 'female';
}

interface GameStateStore {
  // Table info
  tableId: string | null;
  tableName: string | null;
  minBalance: number;

  // Current round state
  round: RoundState | null;
  phase: GamePhase;
  buttonPosition: number;

  // Player states (by seat position)
  players: Map<number, PlayerGameState>;
  mySeatPosition: number | null;

  // Dealer state
  dealer: DealerState;
  dealerPersona: DealerPersona | null;

  // Turn tracking
  currentTurnSeat: number | null;
  currentHandIndex: number;
  turnDeadline: number | null;
  availableActions: string[];

  // Betting
  bettingDeadline: number | null;

  // Insurance
  insuranceOffered: boolean;
  insuranceDeadline: number | null;
  // Track when user has made an insurance decision (used to hide modal immediately)
  // This is set by both manual handlers and autoplay, cleared when insuranceOffered becomes false
  insuranceDecisionPending: boolean;

  // Insurance countdown timer (per-player)
  insuranceCountdownDeadline: number | null;
  insuranceCountdownActive: boolean;
  insuranceCountdownSeat: number | null;

  // Betting countdown timer
  bettingCountdownDeadline: number | null;
  bettingCountdownActive: boolean;
  playersNeedingBets: number[];

  // Player turn countdown timer (per-player circular countdown around avatar)
  turnCountdownDeadline: number | null;
  turnCountdownActive: boolean;
  turnCountdownSeat: number | null;

  // Turn signal — monotonically increasing counter bumped on every TURN_STARTED.
  // ControlPanel watches this to guarantee actionPending clears even when other
  // useEffect deps (availableActions, myPlayerState) happen to pass Object.is checks.
  turnSignal: number;

  // Shoe penetration
  cardsDealt: number; // Number of cards dealt from shoe
  penetration: number; // Percentage (0-100)
  reshufflePending: boolean; // True when threshold crossed, shows "RESHUFFLE"

  // Connection
  isConnected: boolean;
  lastEventSequence: number;

  // DEAD CODE - COMMENTED OUT 2026-05-12
  // Keyed resync system replaced by lifecycle registry approach
  // Delete after verifying no regressions
  // awaitedResyncKey: number;

  // Animation tracking - kitties that have been cleared by payout animation
  // Format: "seatPosition-handIndex" (e.g., "1-0", "2-1")
  clearedKitties: Set<string>;

  // Animation tracking - kitties currently being animated (hidden during animation)
  // React chips are hidden while WAAPI flying chips are visible
  animatingKitties: Set<string>;

  // Track which seats are currently occupied (from setSeatedPlayers)
  // Used to skip event processing for players who have left
  occupiedSeats: Set<number>;

  // Track which opponent seats have bet animations in progress
  // Chips are hidden in React during animation, revealed when animation completes
  animatingOpponentBets: Set<number>;

  // Connection health warnings per seat (from server ACK monitoring)
  // Key: seatPosition (1-7), Value: true if warning is active
  connectionWarnings: Map<number, boolean>;

  // ===========================================
  // SERVER-AUTHORITATIVE CONTROL PANEL STATE
  // ===========================================
  // The server broadcasts the exact button states for each player.
  // Client disables all buttons on click, re-enables per server instructions.
  // Key: seatPosition (1-7), Value: SeatControlState
  seatControls: Map<number, SeatControlState>;

  // Sidebets - current user's active sidebets (placed this round)
  // Key: sidebetType (e.g., 'perfect-pairs'), Value: sidebet state
  mySidebets: Map<string, {
    amount: number;
    rounds: number;  // -1 = ongoing, positive = countdown
    resolved?: boolean;
    outcome?: 'win' | 'lose';
    winType?: string;
    payout?: number;
  }>;

  // Sidebet Configurations - "set and forget" configs that auto-place each round
  // Key: sidebetType, Value: configuration (percentage-based, not absolute amount)
  mySidebetConfigs: Map<string, {
    percentage: number;  // 10, 25, 50, or 100
    rounds: number;      // -1 = ongoing, positive = countdown
    chipsPlacedForCountdown: boolean;  // True when chips have arrived at icon this round
  }>;

  // ===========================================
  // SERVER RECOMMENDATIONS (Client-Driven Autoplay / BUDDY!)
  // ===========================================
  // Server sends ACTION_RECOMMENDATION and INSURANCE_RECOMMENDATION events.
  // Both autoplay and BUDDY! consume the same server-authoritative recommendations.
  // This is the SINGLE SOURCE OF TRUTH for all recommendations.
  serverRecommendation: {
    action: 'HIT' | 'STAND' | 'DOUBLE' | 'SPLIT' | 'SURRENDER' | null;
    reason: string;
    isDeviation: boolean;
    seatPosition: number | null;
    handIndex: number | null;
  } | null;
  serverInsuranceRecommendation: {
    action: 'INSURANCE' | 'NO_INSURANCE' | null;
    reason: string;
    isDeviation: boolean;
    seatPosition: number | null;
  } | null;

  // Actions
  setTable: (tableId: string, tableName: string, minBalance: number, phase?: GamePhase) => void;
  setPenetration: (cardsDealt: number, penetration: number, reshufflePending: boolean) => void;
  setDealerPersona: (persona: DealerPersona | null) => void;
  setMySeat: (seatPosition: number | null) => void;
  /** Initialize a solo player entry at a seat position (prevents empty seat rendering) */
  initializeSoloPlayer: (seatPosition: number, options?: { username?: string; avatarUrl?: string | null }) => void;
  /** Populate players map from table seats data (for usernames before game events) */
  setSeatedPlayers: (seats: Array<{ seatIndex: number; userId: string; username: string; avatarUrl?: string | null; hands?: Array<{ bet?: number; cards?: unknown[] }> } | null>) => void;
  processEvent: (event: BlackjackEvent) => void;
  /** Apply deferred score calculation after a deal animation completes. */
  recalculateScore: (target: 'player' | 'dealer', seatPosition?: number, handIndex?: number) => void;
  resetRound: () => void;
  disconnect: () => void;
  clearState: () => void;
  /** Clear game state for resync (preserves tableId, tableName, mySeatPosition, isConnected) */
  clearGameStateForResync: () => void;
  // DEAD CODE - COMMENTED OUT 2026-05-12
  // setAwaitedResyncKey: (key: number) => void;
  markKittyCleared: (seatPosition: number, handIndex: number) => void;
  markKittyAnimating: (seatPosition: number, handIndex: number) => void;
  clearInsuranceBet: (seatPosition: number) => void;
  /** Mark opponent bet animation as started (hides React chips) */
  startOpponentBetAnimation: (seatPosition: number) => void;
  /** Mark opponent bet animation as complete (reveals React chips) */
  endOpponentBetAnimation: (seatPosition: number) => void;
  /** Clear player hands when player rake animation starts */
  clearHandsForRake: () => void;
  /** Clear dealer state when dealer rake animation starts */
  clearDealerForRake: () => void;
  /** Sync full game state from backend table snapshot (for mid-hand joins) */
  syncFromTableState: (tableState: {
    seats: Array<{
      seatIndex: number;
      userId: string;
      username: string;
      avatarUrl: string | null;
      hands: Array<{
        cards: Array<{ rank: string; suit: string; faceUp: boolean }>;
        bet: number;
        betChips?: number[];
        isDoubled?: boolean;
        isSplit?: boolean;
        isSurrendered?: boolean;
        isStanding?: boolean;
        isBusted?: boolean;
        result?: string;
      }>;
      currentHandIndex: number;
      totalBet: number;
      isActive: boolean;
      isTurn: boolean;
      insuranceBet: number;
      insuranceDeclined: boolean;
      sidebets?: Record<string, {
        amount: number;
        percentage: number;
        rounds: number;
        resolved?: boolean;
        outcome?: 'win' | 'lose';
        winType?: string;
        payout?: number;
      }>;
    } | null>;
    dealer: {
      cards: Array<{ rank: string; suit: string; faceUp: boolean }>;
      score?: number;
      isBusted?: boolean;
    };
    currentSeatIndex: number;
    buttonSeatIndex: number;
    availableActions?: string[];
  }) => void;
  /** Update connection warning state for a seat (from server ACK monitoring) */
  setConnectionWarning: (seatPosition: number, userId: string, active: boolean) => void;
  /** Clear all connection warnings (e.g., when leaving table) */
  clearConnectionWarnings: () => void;
  /** Set or update a sidebet for the current user */
  setSidebet: (sidebetType: string, amount: number, rounds: number) => void;
  /** Update sidebet resolution state after SIDEBET_RESOLVED event */
  updateSidebetResolution: (sidebetType: string, outcome: 'win' | 'lose', winType?: string, payout?: number) => void;
  /** Update sidebet resolution state on a specific player (for reconciler visual feedback) */
  updatePlayerSidebetResolution: (seatPosition: number, sidebetType: string, outcome: 'win' | 'lose') => void;
  /** Reset all players' sidebet resolution states (call at DEALING_STARTED to clear win/lose borders) */
  resetAllPlayerSidebetResolutions: () => void;
  /** Remove a sidebet (when cancelled or expired) */
  removeSidebet: (sidebetType: string) => void;
  /** Clear all sidebets (on disconnect or table leave) */
  clearSidebets: () => void;

  // Sidebet Configuration Actions ("set and forget")
  /** Set or update a sidebet configuration (percentage-based, auto-places each round) */
  setSidebetConfig: (sidebetType: string, percentage: number, rounds: number) => void;
  /** Remove a sidebet configuration */
  removeSidebetConfig: (sidebetType: string) => void;
  /** Decrement rounds for all sidebet configs (call after each round) - DEPRECATED, use decrementSidebetConfig */
  decrementSidebetConfigRounds: () => void;
  /** Decrement rounds for a specific sidebet config (call when sidebet chips are resolved) */
  decrementSidebetConfig: (sidebetType: string) => void;
  /** Mark that chips have been placed for this sidebet (arrival at icon) - required before decrement */
  markSidebetChipsPlaced: (sidebetType: string) => void;
  /** Clear all sidebet configurations */
  clearSidebetConfigs: () => void;

  // Server Recommendation Actions
  /** Set server action recommendation (from ACTION_RECOMMENDATION event) */
  setServerRecommendation: (
    action: 'HIT' | 'STAND' | 'DOUBLE' | 'SPLIT' | 'SURRENDER',
    reason: string,
    isDeviation: boolean,
    seatPosition: number,
    handIndex: number
  ) => void;
  /** Set server insurance recommendation (from INSURANCE_RECOMMENDATION event) */
  setServerInsuranceRecommendation: (
    action: 'INSURANCE' | 'NO_INSURANCE',
    reason: string,
    isDeviation: boolean,
    seatPosition: number
  ) => void;
  /** Clear action recommendation (when turn ends or action taken) */
  clearServerRecommendation: () => void;
  /** Clear insurance recommendation (when insurance decision made) */
  clearServerInsuranceRecommendation: () => void;
  /** Mark that an insurance decision has been made (hides modal immediately) */
  setInsuranceDecisionPending: (pending: boolean) => void;
}

// ===========================================
// EVENT PROCESSORS
// ===========================================

function processRoundEvent(_state: GameStateStore, event: BlackjackEvent): Partial<GameStateStore> {
  switch (event.type) {
    case 'ROUND_STARTED': {
      const round = createRoundState(event.tableId, event.roundNumber, event.buttonPosition);
      // NOTE: We intentionally do NOT reset players' hands or dealer here.
      // This allows previous round's cards and scores to persist visually
      // until the cards are raked away and DEALING_STARTED fires.
      // However, we DO clear confirmedBet to prevent duplicate chips in kitty
      // when user places a new bet while old hands are still visible.
      // NOTE: Do NOT reset clearedKitties here! Hands are not reset until DEALING_STARTED,
      // so resetting clearedKitties would cause chips to reappear for hands that have
      // outcomes but are no longer "cleared" in this new empty Set.
      const players = new Map(_state.players);
      for (const [seatPos, player] of players) {
        players.set(seatPos, {
          ...player,
          confirmedBet: 0, // Clear previous round's bet to prevent duplicate chip display
          stagedBet: 0, // Clear any staged bet from previous round
        });
      }
      return {
        round,
        players,
        phase: GamePhase.BETTING,
        buttonPosition: event.buttonPosition,
        // Keep dealer from previous round (scores persist until rake)
        currentTurnSeat: null,
        currentHandIndex: 0,
        turnDeadline: null,
        availableActions: [],
        // Keep clearedKitties - they persist until DEALING_STARTED resets hands
        animatingKitties: new Set<string>(),
        animatingOpponentBets: new Set<number>(), // Clear any stale bet animations
        bettingCountdownDeadline: null,
        bettingCountdownActive: false,
        playersNeedingBets: [],
        // Clear turn countdown - no longer in player turn phase
        turnCountdownDeadline: null,
        turnCountdownActive: false,
        turnCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'BETTING_OPENED':
      return {
        phase: GamePhase.BETTING,
        bettingDeadline: event.deadline,
        lastEventSequence: event.sequenceNumber,
      };

    case 'BETTING_CLOSED':
      return {
        bettingDeadline: null,
        bettingCountdownDeadline: null,
        bettingCountdownActive: false,
        playersNeedingBets: [],
        lastEventSequence: event.sequenceNumber,
      };

    case 'DEALING_STARTED': {
      // NOW reset players' hands and dealer - cards are being dealt fresh
      // This is deferred from ROUND_STARTED to allow previous round's
      // cards and scores to persist until this point (after rake animation)
      const players = new Map(_state.players);
      for (const [seatPos, player] of players) {
        players.set(seatPos, {
          ...player,
          hands: [], // Clear previous round's hands
          status: PlayerGameStatus.BETTING, // Reset status
          currentHandIndex: 0,
          insuranceDecided: false,
          insuranceBet: 0,
        });
      }
      return {
        phase: GamePhase.DEALING,
        players,
        dealer: createEmptyDealerState(),
        // Reset cleared/animating kitties now that hands are reset
        clearedKitties: new Set<string>(),
        animatingKitties: new Set<string>(),
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'PLAYER_TURNS_STARTED':
      console.log('[PLAYER_TURNS_STARTED] Setting phase to PLAYING, firstToAct:', event.firstToAct);
      return {
        phase: GamePhase.PLAYING,
        currentTurnSeat: event.firstToAct,
        lastEventSequence: event.sequenceNumber,
      };

    case 'DEALER_TURN_STARTED':
      return {
        phase: GamePhase.DEALER_TURN,
        currentTurnSeat: null,
        lastEventSequence: event.sequenceNumber,
      };

    case 'ROUND_COMPLETED': {
      // Round is done - backend accepts new bets, so transition to BETTING
      // Keep player hands visible so they can see results
      // CRITICAL: Clear confirmedBet so players can place bets for the next round
      // Without this, canPlaceBet stays false because currentBet !== 0
      const players = new Map(_state.players);
      for (const [seatPos, player] of players) {
        players.set(seatPos, {
          ...player,
          confirmedBet: 0, // Clear for next round betting
        });
      }
      return {
        players,
        phase: GamePhase.BETTING,
        currentTurnSeat: null,
        turnDeadline: null,
        availableActions: [],
        lastEventSequence: event.sequenceNumber,
      };
    }

    // ===========================================
    // SERVER-AUTHORITATIVE CONTROL PANEL STATE
    // ===========================================
    case 'CONTROLS_UPDATED': {
      // Server broadcasts exact button states for all players
      const seatControls = new Map<number, SeatControlState>();
      for (const ctrl of event.seatControls) {
        seatControls.set(ctrl.seatPosition, ctrl);
      }
      return {
        seatControls,
        lastEventSequence: event.sequenceNumber,
      };
    }

    default:
      return { lastEventSequence: event.sequenceNumber };
  }
}

// Helper to create a default player state
function createDefaultPlayerState(seatPosition: number): PlayerGameState {
  return {
    oduserId: '',
    seatPosition,
    status: PlayerGameStatus.EMPTY,
    stagedBet: 0,
    confirmedBet: 0,
    hands: [],
    currentHandIndex: 0,
    totalBetAmount: 0,
    totalNetResult: 0,
    username: '',
    avatarUrl: null,
    balance: 0,
    isSittingOut: false,
  };
}

// Helper to get or create a player
function getOrCreatePlayer(players: Map<number, PlayerGameState>, seatPosition: number): PlayerGameState {
  let player = players.get(seatPosition);
  if (!player) {
    player = createDefaultPlayerState(seatPosition);
    players.set(seatPosition, player);
  }
  return player;
}

function processBettingEvent(state: GameStateStore, event: BlackjackEvent): Partial<GameStateStore> {
  const players = new Map(state.players);

  switch (event.type) {
    case 'BET_STAGED': {
      let player = getOrCreatePlayer(players, event.seatPosition);
      player = {
        ...player,
        stagedBet: event.totalStaged,
        status: PlayerGameStatus.BETTING,
      };
      players.set(event.seatPosition, player);
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'BET_UNSTAGED': {
      let player = getOrCreatePlayer(players, event.seatPosition);
      player = {
        ...player,
        stagedBet: event.totalStaged,
      };
      players.set(event.seatPosition, player);
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'BET_CONFIRMED': {
      let player = getOrCreatePlayer(players, event.seatPosition);
      player = {
        ...player,
        confirmedBet: event.amount,
        stagedBet: 0,
        status: PlayerGameStatus.READY,
        balance: event.newBalance,
        // Clear any stale hands from previous round - new hands will be created by HAND_BOX_CREATED
        hands: [],
        currentHandIndex: 0,
      };
      players.set(event.seatPosition, player);
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'PLAYER_SIT_OUT': {
      let player = players.get(event.seatPosition);
      if (player) {
        player = {
          ...player,
          isSittingOut: true,
          // Keep player visible - don't set status to EMPTY
          // The isSittingOut flag indicates they're not participating in current round
        };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'PLAYER_SIT_IN': {
      let player = players.get(event.seatPosition);
      if (player) {
        player = {
          ...player,
          isSittingOut: false,
        };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'BETTING_COUNTDOWN': {
      console.log('[BETTING_COUNTDOWN] Event received:', {
        deadline: event.deadline,
        playersWithoutBets: event.playersWithoutBets,
        sequenceNumber: event.sequenceNumber,
      });
      return {
        bettingCountdownDeadline: event.deadline,
        bettingCountdownActive: true,
        playersNeedingBets: event.playersWithoutBets,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'PLAYER_KICKED': {
      // Remove the kicked player from state
      players.delete(event.seatPosition);
      return { players, lastEventSequence: event.sequenceNumber };
    }

    default:
      return { lastEventSequence: event.sequenceNumber };
  }
}

function processDealingEvent(state: GameStateStore, event: BlackjackEvent): Partial<GameStateStore> {
  const players = new Map(state.players);
  let dealer = { ...state.dealer };

  switch (event.type) {
    case 'HAND_BOX_CREATED': {
      let player = getOrCreatePlayer(players, event.seatPosition);
      const hand = createEmptyHandState(event.handIndex, event.bet, event.betChips);
      player = {
        ...player,
        hands: [...player.hands, hand],
        status: PlayerGameStatus.DEALT_IN,
      };
      players.set(event.seatPosition, player);
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'CARD_DEALT': {
      if (event.target === 'dealer') {
        // Enrich card with deterministic visual values (dealer uses seatIndex -1)
        const cardIndex = dealer.cards.length;
        const enrichedCard = enrichCardWithVisuals(event.card, -1, 0, cardIndex);
        const newCards = [...dealer.cards, enrichedCard];
        // Score update is DEFERRED — the deal animation in useGameEvents
        // calls recalculateScore() after the card-back has flown to its
        // target and the card face is revealed. Keeps the score badge
        // from updating before the player can see the card value.
        dealer = {
          ...dealer,
          cards: newCards,
          visibleCards: newCards.filter(c => c.faceUp),
        };
        return { dealer, lastEventSequence: event.sequenceNumber };
      }

      if (event.seatPosition !== undefined) {
        let player = getOrCreatePlayer(players, event.seatPosition);
        // Ensure hand exists
        if (!player.hands[event.handIndex]) {
          // Create the hand if it doesn't exist
          const hand = createEmptyHandState(event.handIndex, 0);
          player = { ...player, hands: [...player.hands, hand] };
        }
        const hands = [...player.hands];
        const hand = { ...hands[event.handIndex] };
        // Enrich card with deterministic visual values
        const cardIndex = hand.cards.length;
        const enrichedCard = enrichCardWithVisuals(
          event.card,
          event.seatPosition - 1, // Convert 1-based seat to 0-based index
          event.handIndex,
          cardIndex
        );
        hand.cards = [...hand.cards, enrichedCard];
        // Score deferred — see dealer comment above.
        hands[event.handIndex] = hand;
        player = { ...player, hands };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'SCORE_UPDATED': {
      if (event.target === 'dealer') {
        dealer = {
          ...dealer,
          score: event.score,
          softScore: event.softScore,
          isSoft: event.isSoft,
          visibleScore: event.score,
        };
        return { dealer, lastEventSequence: event.sequenceNumber };
      }

      if (event.seatPosition !== undefined) {
        let player = players.get(event.seatPosition);
        if (player && player.hands[event.handIndex]) {
          const hands = [...player.hands];
          hands[event.handIndex] = {
            ...hands[event.handIndex],
            score: event.score,
            isSoft: event.isSoft,
          };
          player = { ...player, hands };
          players.set(event.seatPosition, player);
        }
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'INITIAL_DEAL_COMPLETE':
      return { lastEventSequence: event.sequenceNumber };

    default:
      return { lastEventSequence: event.sequenceNumber };
  }
}

function processPlayerTurnEvent(state: GameStateStore, event: BlackjackEvent): Partial<GameStateStore> {
  const players = new Map(state.players);

  switch (event.type) {
    case 'TURN_STARTED': {
      console.log('[TURN_STARTED] Event received:', {
        seatPosition: event.seatPosition,
        handIndex: event.handIndex,
        availableActions: event.availableActions,
        mySeatPosition: state.mySeatPosition,
        sequenceNumber: event.sequenceNumber,
        lastEventSequence: state.lastEventSequence,
      });
      let player = getOrCreatePlayer(players, event.seatPosition);
      player = {
        ...player,
        status: PlayerGameStatus.MY_TURN,
        currentHandIndex: event.handIndex,
      };
      players.set(event.seatPosition, player);
      const result = {
        players,
        currentTurnSeat: event.seatPosition,
        currentHandIndex: event.handIndex,
        turnDeadline: event.deadline,
        availableActions: event.availableActions,
        turnSignal: state.turnSignal + 1,
        // Clear any active turn countdown (new turn, timer resets on backend)
        turnCountdownDeadline: null,
        turnCountdownActive: false,
        turnCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };
      console.log('[TURN_STARTED] Setting state:', {
        currentTurnSeat: result.currentTurnSeat,
        availableActions: result.availableActions,
        turnSignal: result.turnSignal,
        isMyTurn: state.mySeatPosition === event.seatPosition,
      });
      return result;
    }

    case 'ACTION_TAKEN': {
      console.log('[ACTION_TAKEN] Event received:', {
        seatPosition: event.seatPosition,
        handIndex: event.handIndex,
        action: event.action,
        totalHands: players.get(event.seatPosition)?.hands?.length || 0,
        sequenceNumber: event.sequenceNumber,
      });
      let player = players.get(event.seatPosition);
      if (player && player.hands[event.handIndex]) {
        const hands = [...player.hands];
        const hand = { ...hands[event.handIndex] };

        if (event.action === 'double' && event.betAdjustment) {
          hand.bet += event.betAdjustment;
          hand.isDoubled = true;
        }
        if (event.action === 'stand') {
          hand.isStanding = true;
        }

        hands[event.handIndex] = hand;
        player = { ...player, hands };
        players.set(event.seatPosition, player);
      }

      // After a HIT, remove first-two-cards-only actions (double, split, surrender)
      // since the hand now has 3+ cards. The backend only sends availableActions on
      // TURN_STARTED so the frontend must prune them after a hit.
      let updatedActions = state.availableActions;
      if (event.action === 'hit') {
        updatedActions = state.availableActions.filter(
          a => a !== 'double' && a !== 'split' && a !== 'surrender'
        );
      }

      // Clear turn countdown - player took action, timer resets on backend
      return {
        players,
        availableActions: updatedActions,
        turnSignal: state.turnSignal + 1,
        turnCountdownDeadline: null,
        turnCountdownActive: false,
        turnCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'TURN_ENDED': {
      console.log('[TURN_ENDED] Event received:', {
        seatPosition: event.seatPosition,
        sequenceNumber: event.sequenceNumber,
        currentTurnSeat: state.currentTurnSeat,
        currentHandIndex: state.currentHandIndex,
      });
      let player = players.get(event.seatPosition);
      if (player) {
        player = {
          ...player,
          status: PlayerGameStatus.WAITING,
        };
        players.set(event.seatPosition, player);
      }
      return {
        players,
        currentTurnSeat: null,
        turnDeadline: null,
        availableActions: [],
        // Clear turn countdown when turn ends
        turnCountdownDeadline: null,
        turnCountdownActive: false,
        turnCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'HAND_SPLIT': {
      let player = players.get(event.seatPosition);
      if (player) {
        const hands = [...player.hands];
        const originalHand = { ...hands[event.originalHandIndex] };
        // Copy betChips from original hand since split bet matches original bet
        const newHand = createEmptyHandState(event.newHandIndex, event.additionalBet, originalHand.betChips);
        // Enrich split card with deterministic visual values (card 0 of new hand)
        const enrichedSplitCard = enrichCardWithVisuals(
          event.splitCard,
          event.seatPosition - 1, // Convert 1-based seat to 0-based index
          event.newHandIndex,
          0 // First card in new hand
        );
        newHand.cards = [enrichedSplitCard];
        newHand.isSplit = true;
        // Calculate score for the split card so Hand 2 shows its value immediately
        const splitScore = calculateHandScore([event.splitCard]);
        newHand.score = splitScore.score;
        newHand.isSoft = splitScore.isSoft;
        originalHand.cards = originalHand.cards.slice(0, 1);
        originalHand.isSplit = true;
        // Recalculate original hand score (now only has one card)
        const origScore = calculateHandScore(originalHand.cards);
        originalHand.score = origScore.score;
        originalHand.isSoft = origScore.isSoft;
        hands[event.originalHandIndex] = originalHand;
        // Use push to match server-side insertion order (unified with Solo mode).
        // Array order matches visual/creation order, so processing order is correct.
        hands.push(newHand);
        player = { ...player, hands };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'HAND_BUSTED': {
      let player = players.get(event.seatPosition);
      if (player && player.hands[event.handIndex]) {
        const hands = [...player.hands];
        hands[event.handIndex] = {
          ...hands[event.handIndex],
          isBusted: true,
          score: event.finalScore,
        };
        player = { ...player, hands };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'HAND_BLACKJACK': {
      let player = players.get(event.seatPosition);
      if (player && player.hands[event.handIndex]) {
        const hands = [...player.hands];
        hands[event.handIndex] = {
          ...hands[event.handIndex],
          isBlackjack: true,
        };
        player = { ...player, hands };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'HAND_SURRENDERED': {
      let player = players.get(event.seatPosition);
      if (player && player.hands[event.handIndex]) {
        const hands = [...player.hands];
        hands[event.handIndex] = {
          ...hands[event.handIndex],
          isSurrendered: true,
          // NOTE: Do NOT set outcome here. Setting outcome makes handHasOutcome=true,
          // which unlocks the autobet gate before the round completes. The outcome
          // is set by HAND_RESOLVED at round end. The UI renders the SURRENDER label
          // from isSurrendered directly (see SplitHandDisplay).
        };
        player = { ...player, hands };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'PREMATURE_RESOLUTION': {
      let player = players.get(event.seatPosition);
      if (player) {
        const hands = [...player.hands];
        if (hands[event.handIndex]) {
          hands[event.handIndex] = {
            ...hands[event.handIndex],
            outcome: event.outcome,
            netResult: event.payout,
          };
        }
        player = {
          ...player,
          hands,
          status: PlayerGameStatus.PREMATURE_RESOLVED,
        };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'ALL_PLAYER_TURNS_COMPLETE':
      // Update all waiting players to await_dealer
      Array.from(players.entries()).forEach(([seat, player]) => {
        if (player.status === PlayerGameStatus.WAITING) {
          players.set(seat, {
            ...player,
            status: PlayerGameStatus.AWAIT_DEALER,
          });
        }
      });
      return {
        players,
        turnCountdownDeadline: null,
        turnCountdownActive: false,
        turnCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };

    case 'TURN_COUNTDOWN': {
      console.log('[TURN_COUNTDOWN] Event received:', {
        deadline: event.deadline,
        durationMs: event.durationMs,
        seatPosition: event.seatPosition,
        sequenceNumber: event.sequenceNumber,
      });
      return {
        turnCountdownDeadline: event.deadline,
        turnCountdownActive: true,
        turnCountdownSeat: event.seatPosition,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'TURN_TIMEOUT': {
      console.log('[TURN_TIMEOUT] Event received:', {
        seatPosition: event.seatPosition,
        sequenceNumber: event.sequenceNumber,
      });
      // Clear countdown for this player - they've been auto-stood
      return {
        turnCountdownDeadline: null,
        turnCountdownActive: false,
        turnCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };
    }

    default:
      return { lastEventSequence: event.sequenceNumber };
  }
}

function processDealerEvent(state: GameStateStore, event: BlackjackEvent): Partial<GameStateStore> {
  let dealer = { ...state.dealer };

  switch (event.type) {
    case 'DEALER_REVEALS_HOLE': {
      // Update hole card to face up
      const cards = dealer.cards.map((card, idx) =>
        idx === 1 ? { ...card, faceUp: true } : card
      );
      dealer = {
        ...dealer,
        cards,
        holeCardRevealed: true,
        score: event.score,
        isSoft: event.isSoft,
        visibleScore: event.score,
        visibleCards: cards,
      };
      return { dealer, lastEventSequence: event.sequenceNumber };
    }

    case 'DEALER_HITS': {
      // Enrich card with deterministic visual values
      const cardIndex = dealer.cards.length;
      const enrichedCard = enrichCardWithVisuals(event.card, -1, 0, cardIndex);
      const cards = [...dealer.cards, enrichedCard];
      // Score update is DEFERRED — the deal animation in useGameEvents
      // calls recalculateScore() after the card has landed.
      dealer = {
        ...dealer,
        cards,
        visibleCards: cards,
      };
      return { dealer, lastEventSequence: event.sequenceNumber };
    }

    case 'DEALER_STANDS':
      dealer = {
        ...dealer,
        isStanding: true,
        score: event.finalScore,
      };
      return { dealer, lastEventSequence: event.sequenceNumber };

    case 'DEALER_BUSTS':
      dealer = {
        ...dealer,
        isBusted: true,
        score: event.finalScore,
      };
      return { dealer, lastEventSequence: event.sequenceNumber };

    case 'DEALER_BLACKJACK': {
      // When dealer has natural blackjack, reveal the hole card
      const cards = dealer.cards.map((card, idx) =>
        idx === 1 ? { ...card, faceUp: true } : card
      );
      dealer = {
        ...dealer,
        cards,
        isBlackjack: true,
        holeCardRevealed: true,
      };
      return { dealer, lastEventSequence: event.sequenceNumber };
    }

    default:
      return { lastEventSequence: event.sequenceNumber };
  }
}

function processResolutionEvent(state: GameStateStore, event: BlackjackEvent): Partial<GameStateStore> {
  const players = new Map(state.players);

  switch (event.type) {
    case 'HAND_RESOLVED': {
      let player = players.get(event.seatPosition);
      if (player && player.hands[event.handIndex]) {
        const hands = [...player.hands];
        hands[event.handIndex] = {
          ...hands[event.handIndex],
          outcome: event.outcome,
          netResult: event.netResult,
        };
        player = {
          ...player,
          hands,
          status: PlayerGameStatus.RESOLVED,
          totalNetResult: player.totalNetResult + event.netResult,
        };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'CHIPS_PAID': {
      let player = players.get(event.seatPosition);
      if (player) {
        player = {
          ...player,
          balance: event.newBalance,
        };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'CHIPS_COLLECTED': {
      let player = players.get(event.seatPosition);
      if (player) {
        player = {
          ...player,
          balance: event.newBalance,
        };
        players.set(event.seatPosition, player);
      }
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'ALL_RESOLUTIONS_COMPLETE':
      return { lastEventSequence: event.sequenceNumber };

    default:
      return { lastEventSequence: event.sequenceNumber };
  }
}

function processInsuranceEvent(state: GameStateStore, event: BlackjackEvent): Partial<GameStateStore> {
  switch (event.type) {
    case 'INSURANCE_OFFERED': {
      console.log('[INSURANCE_OFFERED] Setting insuranceOffered=true, deadline:', event.deadline);
      return {
        insuranceOffered: true,
        insuranceDeadline: event.deadline,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'INSURANCE_DECISION': {
      // Player made insurance decision - mark as decided, and set insuranceBet if accepted
      console.log('[INSURANCE_DECISION] Event:', {
        seatPosition: event.seatPosition,
        accepted: event.accepted,
        amount: event.amount,
      });
      const players = new Map(state.players);
      let player = getOrCreatePlayer(players, event.seatPosition);
      // Always mark as decided (whether accepted or declined)
      player = {
        ...player,
        insuranceDecided: true,
        ...(event.accepted && event.amount ? { insuranceBet: event.amount } : {}),
      };
      players.set(event.seatPosition, player);
      console.log('[INSURANCE_DECISION] Updated player - decided:', true, 'insuranceBet:', event.amount);
      return { players, lastEventSequence: event.sequenceNumber };
    }

    case 'INSURANCE_RESOLVED': {
      // Insurance phase complete - clear global insurance state
      // NOTE: Do NOT clear player.insuranceBet here - that's done by the animation callback
      // via clearInsuranceBet() after the chip animations complete. This allows the
      // InsuranceChipStack to remain visible during the resolution animation.
      return {
        insuranceOffered: false,
        insuranceDecisionPending: false,
        insuranceDeadline: null,
        insuranceCountdownDeadline: null,
        insuranceCountdownActive: false,
        insuranceCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'INSURANCE_COUNTDOWN': {
      console.log('[INSURANCE_COUNTDOWN] Event received:', {
        deadline: event.deadline,
        durationMs: event.durationMs,
        seatPosition: event.seatPosition,
        sequenceNumber: event.sequenceNumber,
      });
      return {
        insuranceCountdownDeadline: event.deadline,
        insuranceCountdownActive: true,
        insuranceCountdownSeat: event.seatPosition,
        lastEventSequence: event.sequenceNumber,
      };
    }

    case 'INSURANCE_TIMEOUT': {
      console.log('[INSURANCE_TIMEOUT] Event received:', {
        seatPosition: event.seatPosition,
        sequenceNumber: event.sequenceNumber,
      });
      // Clear countdown for this player - they've been auto-declined
      return {
        insuranceCountdownDeadline: null,
        insuranceCountdownActive: false,
        insuranceCountdownSeat: null,
        lastEventSequence: event.sequenceNumber,
      };
    }

    default:
      return { lastEventSequence: event.sequenceNumber };
  }
}

// ===========================================
// STORE
// ===========================================

export const useGameStateStore = create<GameStateStore>((set, get) => ({
  // Initial state
  tableId: null,
  tableName: null,
  minBalance: 0,
  round: null,
  phase: GamePhase.WAITING,
  buttonPosition: 1,
  players: new Map(),
  mySeatPosition: null,
  dealer: createEmptyDealerState(),
  dealerPersona: null,
  currentTurnSeat: null,
  currentHandIndex: 0,
  turnDeadline: null,
  availableActions: [],
  bettingDeadline: null,
  insuranceOffered: false,
  insuranceDecisionPending: false,
  insuranceDeadline: null,
  insuranceCountdownDeadline: null,
  insuranceCountdownActive: false,
  insuranceCountdownSeat: null,
  bettingCountdownDeadline: null,
  bettingCountdownActive: false,
  playersNeedingBets: [],
  turnCountdownDeadline: null,
  turnCountdownActive: false,
  turnCountdownSeat: null,
  turnSignal: 0,
  cardsDealt: 0,
  penetration: 0,
  reshufflePending: false,
  isConnected: false,
  lastEventSequence: -1,
  // DEAD CODE: awaitedResyncKey: 0,
  clearedKitties: new Set<string>(),
  animatingKitties: new Set<string>(),
  occupiedSeats: new Set<number>(),
  animatingOpponentBets: new Set<number>(),
  connectionWarnings: new Map<number, boolean>(),
  seatControls: new Map<number, SeatControlState>(),
  mySidebets: new Map<string, { amount: number; rounds: number; resolved?: boolean; outcome?: 'win' | 'lose'; winType?: string; payout?: number }>(),
  mySidebetConfigs: new Map<string, { percentage: number; rounds: number; chipsPlacedForCountdown: boolean }>(),
  serverRecommendation: null,
  serverInsuranceRecommendation: null,

  // Actions
  setTable: (tableId, tableName, minBalance, phase?) => {
    console.log('[setTable] Setting table state, phase:', phase);
    // NOTE: Do NOT reset insuranceOffered/insuranceDeadline here.
    // Insurance state is controlled by INSURANCE_OFFERED and INSURANCE_RESOLVED events.
    // Resetting here caused the insurance modal to vanish when table_state was broadcast.
    set({
      tableId,
      tableName,
      minBalance,
      isConnected: true,
      ...(phase !== undefined && { phase }),
    });
  },

  setDealerPersona: (persona) => {
    set({ dealerPersona: persona });
  },

  setPenetration: (cardsDealt, penetration, reshufflePending) => {
    set({ cardsDealt, penetration, reshufflePending });
  },

  setMySeat: (seatPosition) => {
    set({ mySeatPosition: seatPosition });
  },

  initializeSoloPlayer: (seatPosition, options = {}) => {
    // Create a player entry for solo mode so RegistryPlayerSeat doesn't render as empty
    const { username = 'PLAYER', avatarUrl } = options;
    const players = new Map(get().players);
    players.set(seatPosition, {
      oduserId: 'solo',
      seatPosition,
      status: PlayerGameStatus.BETTING,
      stagedBet: 0,
      confirmedBet: 0,
      hands: [],
      currentHandIndex: 0,
      totalBetAmount: 0,
      totalNetResult: 0,
      username,
      avatarUrl: avatarUrl ?? null,
      balance: Infinity,
      isSittingOut: false,
    });
    set({ players });
  },

  setSeatedPlayers: (seats) => {
    const state = get();
    const players = new Map(state.players);

    // Track which seat positions are now occupied
    const occupiedSeats = new Set<number>();

    // Iterate through seats and create/update player entries
    for (const seat of seats) {
      if (seat) {
        // seatIndex is 0-based from backend, but seatPosition in frontend is 1-based
        const seatPosition = seat.seatIndex + 1;
        occupiedSeats.add(seatPosition);
        let player = players.get(seatPosition);

        if (!player) {
          // Create new player entry
          player = createDefaultPlayerState(seatPosition);
        }

        // Update with seat data - update identity only
        // IMPORTANT: Do NOT clear hands based on table_state data!
        // table_state can arrive with stale snapshots during gameplay, and clearing hands
        // here causes scorebox to show wrong values (e.g., 7-4-5 showing as "05" because
        // stale table_state cleared the 7 and 4). Hands are managed by game events only:
        // - HAND_BOX_CREATED creates hands
        // - CARD_DEALT adds cards
        // - DEALING_STARTED clears previous round's hands
        players.set(seatPosition, {
          ...player,
          oduserId: seat.userId,
          username: seat.username,
          avatarUrl: seat.avatarUrl ?? null,
          seatPosition,
          // Mark as seated (not empty) if they have a username
          status: seat.username ? PlayerGameStatus.BETTING : player.status,
        });
      }
    }

    // Remove players who are no longer seated (their seat is now null/empty)
    for (const [seatPosition] of players) {
      if (!occupiedSeats.has(seatPosition)) {
        // Seat is now empty - remove the player entry entirely (clears cards, hands, etc.)
        players.delete(seatPosition);
      }
    }

    // Store both the updated players and the occupied seats set
    // occupiedSeats is used by processEvent to skip events for departed players
    set({ players, occupiedSeats });
  },

  processEvent: (event: BlackjackEvent) => {
    const state = get();

    // ROUND_STARTED resets the sequence - always process it and reset tracking
    // This handles the case where a new round starts with sequence 1 but we have
    // a higher lastEventSequence from the previous round
    if (event.type === 'ROUND_STARTED') {
      // Process the event and reset lastEventSequence
      const updates = processRoundEvent(state, event);
      set({ ...updates, lastEventSequence: event.sequenceNumber });
      return;
    }

    // Skip already-processed events
    // BUT: Handle race condition where round N+1's events arrive while round N's batch is still finishing.
    // If event sequence is much lower than lastEventSequence, it's likely a new round, not a duplicate.
    // New rounds typically start at seq 1-5, so if lastEventSequence is >20 and event seq is <10, accept it.
    const sequenceGap = state.lastEventSequence - event.sequenceNumber;
    const likelyNewRound = sequenceGap > 15 && event.sequenceNumber < 10;

    if (event.sequenceNumber <= state.lastEventSequence && !likelyNewRound) {
      console.log('[processEvent] SKIPPING event (already processed):', {
        type: event.type,
        sequenceNumber: event.sequenceNumber,
        lastEventSequence: state.lastEventSequence,
      });
      return;
    }

    // If this looks like a new round (big sequence gap), reset to accept fresh events
    if (likelyNewRound) {
      console.log('[processEvent] Detected new round (sequence reset):', {
        type: event.type,
        sequenceNumber: event.sequenceNumber,
        lastEventSequence: state.lastEventSequence,
        gap: sequenceGap,
      });
      // Don't update lastEventSequence here - let normal processing do it
      // Just continue processing this event
    }

    // Skip events for players who have left the table
    // This prevents re-creating player entries from stale events after setSeatedPlayers removed them
    const eventWithSeat = event as { seatPosition?: number };
    if (eventWithSeat.seatPosition !== undefined &&
        state.occupiedSeats.size > 0 &&
        !state.occupiedSeats.has(eventWithSeat.seatPosition)) {
      console.log('[processEvent] SKIPPING event for departed player:', {
        type: event.type,
        seatPosition: eventWithSeat.seatPosition,
        occupiedSeats: Array.from(state.occupiedSeats),
      });
      // Still update lastEventSequence to avoid reprocessing
      set({ lastEventSequence: event.sequenceNumber });
      return;
    }

    let updates: Partial<GameStateStore> = {};

    switch (event.category) {
      case 'round':
        updates = processRoundEvent(state, event);
        break;
      case 'betting':
        updates = processBettingEvent(state, event);
        break;
      case 'dealing':
        updates = processDealingEvent(state, event);
        break;
      case 'player_turn':
        updates = processPlayerTurnEvent(state, event);
        break;
      case 'dealer':
        updates = processDealerEvent(state, event);
        break;
      case 'resolution':
        updates = processResolutionEvent(state, event);
        break;
      case 'insurance':
        updates = processInsuranceEvent(state, event);
        break;
    }

    set(updates);

  },

  resetRound: () => {
    set({
      round: null,
      phase: GamePhase.WAITING,
      players: new Map(),
      dealer: createEmptyDealerState(),
      currentTurnSeat: null,
      currentHandIndex: 0,
      turnDeadline: null,
      availableActions: [],
      turnSignal: 0,
      bettingDeadline: null,
      insuranceOffered: false,
      insuranceDeadline: null,
      insuranceCountdownDeadline: null,
      insuranceCountdownActive: false,
      insuranceCountdownSeat: null,
      bettingCountdownDeadline: null,
      bettingCountdownActive: false,
      playersNeedingBets: [],
      turnCountdownDeadline: null,
      turnCountdownActive: false,
      turnCountdownSeat: null,
    });
  },

  disconnect: () => {
    set({
      isConnected: false,
      tableId: null,
      tableName: null,
    });
  },

  clearState: () => {
    set({
      tableId: null,
      tableName: null,
      minBalance: 0,
      round: null,
      phase: GamePhase.WAITING,
      buttonPosition: 1,
      players: new Map(),
      mySeatPosition: null,
      dealer: createEmptyDealerState(),
      dealerPersona: null,
      currentTurnSeat: null,
      currentHandIndex: 0,
      turnDeadline: null,
      availableActions: [],
      turnSignal: 0,
      bettingDeadline: null,
      insuranceOffered: false,
      insuranceDeadline: null,
      insuranceCountdownDeadline: null,
      insuranceCountdownActive: false,
      insuranceCountdownSeat: null,
      bettingCountdownDeadline: null,
      bettingCountdownActive: false,
      playersNeedingBets: [],
      turnCountdownDeadline: null,
      turnCountdownActive: false,
      turnCountdownSeat: null,
      isConnected: false,
      lastEventSequence: -1,
      clearedKitties: new Set<string>(),
      animatingKitties: new Set<string>(),
      occupiedSeats: new Set<number>(),
      animatingOpponentBets: new Set<number>(),
      connectionWarnings: new Map<number, boolean>(),
      seatControls: new Map<number, SeatControlState>(),
      mySidebets: new Map(),
      mySidebetConfigs: new Map(),  // Also clear configs on disconnect
    });
  },

  clearGameStateForResync: () => {
    // Clear game state but preserve connection info (tableId, tableName, mySeatPosition, isConnected)
    // Used when tab becomes hidden - we wipe state so no stale data persists
    const state = get();
    console.log('[GameState] clearGameStateForResync - CLEARING:', {
      phase: state.phase,
      playerCount: state.players.size,
      dealerCards: state.dealer.cards.length,
      currentTurnSeat: state.currentTurnSeat,
      round: state.round?.roundNumber,
      lastEventSequence: state.lastEventSequence,
    });
    set({
      round: null,
      phase: GamePhase.WAITING,
      players: new Map(),
      dealer: createEmptyDealerState(),
      currentTurnSeat: null,
      currentHandIndex: 0,
      turnDeadline: null,
      availableActions: [],
      turnSignal: 0,
      bettingDeadline: null,
      insuranceOffered: false,
      insuranceDeadline: null,
      insuranceCountdownDeadline: null,
      insuranceCountdownActive: false,
      insuranceCountdownSeat: null,
      bettingCountdownDeadline: null,
      bettingCountdownActive: false,
      playersNeedingBets: [],
      turnCountdownDeadline: null,
      turnCountdownActive: false,
      turnCountdownSeat: null,
      lastEventSequence: -1,
      clearedKitties: new Set<string>(),
      animatingKitties: new Set<string>(),
      occupiedSeats: new Set<number>(),
      animatingOpponentBets: new Set<number>(),
      seatControls: new Map<number, SeatControlState>(),
      // Preserve: tableId, tableName, minBalance, mySeatPosition, isConnected, dealerPersona
      // Preserve: mySidebets, mySidebetConfigs (player's sidebet configuration)
    });
    console.log('[GameState] clearGameStateForResync - CLEARED (players=0, dealer=0, seq=-1)');
  },

  // DEAD CODE - COMMENTED OUT 2026-05-12
  // setAwaitedResyncKey: (key) => {
  //   console.log('[GameState] setAwaitedResyncKey:', key);
  //   set({ awaitedResyncKey: key });
  // },

  markKittyAnimating: (seatPosition, handIndex) => {
    const { animatingKitties } = get();
    const newSet = new Set(animatingKitties);
    newSet.add(`${seatPosition}-${handIndex}`);
    set({ animatingKitties: newSet });
  },

  markKittyCleared: (seatPosition, handIndex) => {
    const key = `${seatPosition}-${handIndex}`;
    const { clearedKitties } = get();
    const newSet = new Set(clearedKitties);
    newSet.add(key);
    set({ clearedKitties: newSet });
  },

  recalculateScore: (target, seatPosition, handIndex) => {
    if (target === 'dealer') {
      const { dealer } = get();
      const { score, isSoft, softScore } = calculateHandScore(dealer.cards.filter(c => c.faceUp));
      set({
        dealer: {
          ...dealer,
          score,
          softScore,
          isSoft,
          visibleScore: score,
        },
      });
    } else if (seatPosition !== undefined && handIndex !== undefined) {
      const { players } = get();
      const player = players.get(seatPosition);
      if (!player || !player.hands[handIndex]) return;
      const hands = [...player.hands];
      const hand = { ...hands[handIndex] };
      const scoreResult = calculateHandScore(hand.cards);
      hand.score = scoreResult.score;
      hand.isSoft = scoreResult.isSoft;
      hands[handIndex] = hand;
      const newPlayers = new Map(players);
      newPlayers.set(seatPosition, { ...player, hands });
      set({ players: newPlayers });
    }
  },

  clearInsuranceBet: (seatPosition) => {
    const { players } = get();
    const player = players.get(seatPosition);
    // Clear if player has any insurance state (insuranceBet OR insuranceDecided)
    // This ensures cleanup even if insuranceBet is 0 or falsy
    if (player && (player.insuranceBet || player.insuranceDecided)) {
      const newPlayers = new Map(players);
      newPlayers.set(seatPosition, {
        ...player,
        insuranceBet: undefined,
        // Keep insuranceDecided=true to prevent badge from reappearing
        // while insuranceOffered is still true during resolution
        insuranceDecided: true,
        insuranceResult: undefined,
      });
      set({ players: newPlayers });
    }
  },

  startOpponentBetAnimation: (seatPosition) => {
    const { animatingOpponentBets } = get();
    const newSet = new Set(animatingOpponentBets);
    newSet.add(seatPosition);
    set({ animatingOpponentBets: newSet });
  },

  endOpponentBetAnimation: (seatPosition) => {
    const { animatingOpponentBets } = get();
    const newSet = new Set(animatingOpponentBets);
    newSet.delete(seatPosition);
    set({ animatingOpponentBets: newSet });
  },

  clearHandsForRake: () => {
    // Clear player hands at the START of player rake animation
    // Dealer is cleared separately when dealer rake starts
    // Also clear confirmedBet to prevent SplitChipStacks from rendering
    // chips based on confirmedBet when hands is empty
    // IMPORTANT: Only clear current user's seat - preserve opponents' bets!
    const { players, mySeatPosition } = get();
    if (mySeatPosition === null) return;

    const newPlayers = new Map(players);
    const myPlayer = newPlayers.get(mySeatPosition);
    if (myPlayer) {
      newPlayers.set(mySeatPosition, {
        ...myPlayer,
        hands: [],
        currentHandIndex: 0,
        confirmedBet: 0,
      });
      set({ players: newPlayers });
    }
  },

  clearDealerForRake: () => {
    // Clear dealer state at the START of dealer rake animation
    set({ dealer: createEmptyDealerState() });
  },

  syncFromTableState: (tableState) => {
    console.log('[syncFromTableState] Syncing full game state for mid-hand join');

    const players = new Map<number, PlayerGameState>();
    const occupiedSeats = new Set<number>();

    // Convert backend seats to frontend player state
    for (const seat of tableState.seats) {
      if (seat) {
        const seatPosition = seat.seatIndex + 1; // Convert 0-based to 1-based
        occupiedSeats.add(seatPosition);

        // Convert hands from backend format to frontend format
        const hands: HandState[] = seat.hands.map((hand, handIdx) => {
          // Enrich cards with deterministic visual values (simulates server-authoritative visuals)
          const cards = hand.cards.map((card, cardIdx) =>
            enrichCardWithVisuals(card, seat.seatIndex, handIdx, cardIdx)
          );
          const { score, isSoft } = calculateHandScore(cards.filter(c => c.faceUp));
          return {
            index: handIdx,
            cards,
            bet: hand.bet,
            betChips: hand.betChips || [],
            score,
            isSoft,
            isDoubled: hand.isDoubled || false,
            isSplit: hand.isSplit || false,
            isSurrendered: hand.isSurrendered || false,
            isStanding: hand.isStanding || false,
            isBusted: hand.isBusted || false,
            isBlackjack: hand.cards.length === 2 && score === 21 && !hand.isSplit,
            outcome: hand.result as HandState['outcome'],
          };
        });

        // Determine player status based on game state
        let status = PlayerGameStatus.BETTING;
        if (seat.isTurn) {
          status = PlayerGameStatus.MY_TURN;
        } else if (seat.isActive && hands.length > 0) {
          status = PlayerGameStatus.WAITING;
        }

        // Preserve existing insuranceDecided if already true (set by INSURANCE_DECISION event)
        // This prevents the badge from reappearing during animation when table_state arrives
        const existingPlayer = get().players.get(seatPosition);
        const insuranceDecidedFromServer = seat.insuranceDeclined || seat.insuranceBet > 0;
        const insuranceDecided = existingPlayer?.insuranceDecided || insuranceDecidedFromServer;

        // Preserve existing hands if server sends empty hands during transitional phase
        // This prevents scorebox from showing "--" when table_state arrives before game_events
        const existingHasCards = existingPlayer?.hands?.some(h => h.cards.length > 0);
        const preserveHands = hands.length === 0 && existingHasCards;
        const finalHands = preserveHands ? existingPlayer!.hands : hands;
        const finalHandIndex = seat.currentHandIndex ?? existingPlayer?.currentHandIndex ?? 0;

        // Extract sidebets from seat and convert to activeSidebets array format
        // This enables the SidebetIconReconciler to show win/lose border colors
        const activeSidebets: Array<{
          type: string;
          amount: number;
          rounds: number;
          resolved?: boolean;
          outcome?: 'win' | 'lose';
        }> = seat.sidebets
          ? Object.entries(seat.sidebets).map(([type, sidebet]) => ({
              type,
              amount: sidebet.amount,
              rounds: sidebet.rounds,
              resolved: sidebet.resolved,
              outcome: sidebet.outcome,
            }))
          : [];

        players.set(seatPosition, {
          oduserId: seat.userId,
          seatPosition,
          status,
          stagedBet: 0,
          confirmedBet: seat.totalBet,
          hands: finalHands,
          currentHandIndex: finalHandIndex,
          insuranceBet: seat.insuranceBet || undefined,
          insuranceDecided,
          totalBetAmount: seat.totalBet,
          totalNetResult: 0,
          username: seat.username,
          avatarUrl: seat.avatarUrl,
          balance: 0, // Balance not included in seat data
          isSittingOut: false,
          // activeSidebets for reconciler - not in PlayerGameState interface but used by pixi-v2
          activeSidebets,
        } as PlayerGameState & { activeSidebets: typeof activeSidebets });
      }
    }

    // Convert dealer state (use seatIndex -1 for dealer)
    const dealerCards = tableState.dealer.cards.map((card, cardIdx) =>
      enrichCardWithVisuals(card, -1, 0, cardIdx)
    );
    const visibleCards = dealerCards.filter(c => c.faceUp);
    const { score: dealerScore, isSoft: dealerIsSoft } = calculateHandScore(visibleCards);
    const holeCardRevealed = dealerCards.length > 1 && dealerCards[1]?.faceUp === true;

    const dealer: DealerState = {
      cards: dealerCards,
      score: holeCardRevealed ? calculateHandScore(dealerCards).score : dealerScore,
      softScore: undefined,
      isSoft: dealerIsSoft,
      holeCardRevealed,
      isBlackjack: dealerCards.length === 2 && calculateHandScore(dealerCards).score === 21,
      isBusted: tableState.dealer.isBusted || false,
      isStanding: false,
      visibleScore: dealerScore,
      visibleCards,
    };

    // Set current turn seat (convert 0-based to 1-based)
    const currentTurnSeat = tableState.currentSeatIndex >= 0 ? tableState.currentSeatIndex + 1 : null;
    const buttonPosition = tableState.buttonSeatIndex >= 0 ? tableState.buttonSeatIndex + 1 : 1;

    console.log('[syncFromTableState] Synced:', {
      playerCount: players.size,
      dealerCards: dealerCards.length,
      currentTurnSeat,
      buttonPosition,
      availableActions: tableState.availableActions,
    });

    set({
      players,
      occupiedSeats,
      dealer,
      currentTurnSeat,
      buttonPosition,
      // Also set current hand index for the active player
      currentHandIndex: currentTurnSeat && players.has(currentTurnSeat)
        ? players.get(currentTurnSeat)!.currentHandIndex
        : 0,
      // Set available actions for mid-hand joins
      availableActions: tableState.availableActions || [],
    });
  },

  setConnectionWarning: (seatPosition, userId, active) => {
    const connectionWarnings = new Map(get().connectionWarnings);
    if (active) {
      connectionWarnings.set(seatPosition, true);
    } else {
      connectionWarnings.delete(seatPosition);
    }
    set({ connectionWarnings });
  },

  clearConnectionWarnings: () => {
    set({ connectionWarnings: new Map<number, boolean>() });
  },

  setSidebet: (sidebetType, amount, rounds) => {
    const { mySidebets } = get();
    const newMap = new Map(mySidebets);
    newMap.set(sidebetType, {
      amount,
      rounds,
      resolved: false,
    });
    set({ mySidebets: newMap });
  },

  updateSidebetResolution: (sidebetType, outcome, winType, payout) => {
    const { mySidebets } = get();
    const sidebet = mySidebets.get(sidebetType);
    if (!sidebet) return;

    const newMap = new Map(mySidebets);
    newMap.set(sidebetType, {
      ...sidebet,
      resolved: true,
      outcome,
      winType,
      payout,
    });
    set({ mySidebets: newMap });
  },

  updatePlayerSidebetResolution: (seatPosition, sidebetType, outcome) => {
    // Update the player's activeSidebets for reconciler visual feedback
    // This enables the SidebetIconReconciler to show win/lose border colors
    const { players } = get();
    const player = players.get(seatPosition);
    if (!player) return;

    // Get existing activeSidebets (cast since it's not in interface)
    const activeSidebets = (player as any).activeSidebets as Array<{
      type: string;
      amount: number;
      rounds: number;
      resolved?: boolean;
      outcome?: 'win' | 'lose';
    }> | undefined;

    if (!activeSidebets) return;

    // Find and update the matching sidebet
    const updatedSidebets = activeSidebets.map(sb =>
      sb.type === sidebetType
        ? { ...sb, resolved: true, outcome }
        : sb
    );

    // Update player with new sidebets
    const newPlayers = new Map(players);
    newPlayers.set(seatPosition, {
      ...player,
      activeSidebets: updatedSidebets,
    } as any);
    set({ players: newPlayers });
  },

  resetAllPlayerSidebetResolutions: () => {
    // Reset all players' sidebet resolved/outcome to clear win/lose borders
    // Called at DEALING_STARTED to reset visual state for new round
    const { players, mySidebets } = get();
    const newPlayers = new Map<number, PlayerGameState>();

    // Reset other players' activeSidebets
    for (const [seatPosition, player] of players) {
      const activeSidebets = (player as any).activeSidebets as Array<{
        type: string;
        amount: number;
        rounds: number;
        resolved?: boolean;
        outcome?: 'win' | 'lose';
      }> | undefined;

      if (activeSidebets && activeSidebets.length > 0) {
        // Reset resolved/outcome to undefined for all sidebets
        const resetSidebets = activeSidebets.map(sb => ({
          ...sb,
          resolved: undefined,
          outcome: undefined,
        }));
        newPlayers.set(seatPosition, {
          ...player,
          activeSidebets: resetSidebets,
        } as any);
      } else {
        newPlayers.set(seatPosition, player);
      }
    }

    // Reset current user's mySidebets
    const newMySidebets = new Map(mySidebets);
    for (const [sidebetType, sidebet] of newMySidebets) {
      newMySidebets.set(sidebetType, {
        ...sidebet,
        resolved: undefined,
        outcome: undefined,
      });
    }

    set({ players: newPlayers, mySidebets: newMySidebets });
  },

  removeSidebet: (sidebetType) => {
    const { mySidebets } = get();
    const newMap = new Map(mySidebets);
    newMap.delete(sidebetType);
    set({ mySidebets: newMap });
  },

  clearSidebets: () => {
    set({ mySidebets: new Map() });
  },

  // Sidebet Configuration Actions ("set and forget")
  setSidebetConfig: (sidebetType, percentage, rounds) => {
    console.log(`[GameState] setSidebetConfig: ${sidebetType}, ${percentage}%, ${rounds === -1 ? 'ongoing' : rounds + ' rounds'}`);
    const { mySidebetConfigs } = get();
    const newMap = new Map(mySidebetConfigs);
    // Initialize chipsPlacedForCountdown as false - will be set true when chips arrive at icon
    newMap.set(sidebetType, { percentage, rounds, chipsPlacedForCountdown: false });
    set({ mySidebetConfigs: newMap });
  },

  // Mark that chips have been placed for this sidebet (arrival at icon)
  // This is the first half of a "complete round" - the second half is resolution
  markSidebetChipsPlaced: (sidebetType) => {
    const { mySidebetConfigs } = get();
    const config = mySidebetConfigs.get(sidebetType);

    if (!config) {
      console.warn(`[GameState] markSidebetChipsPlaced: ${sidebetType} not found`);
      return;
    }

    const newMap = new Map(mySidebetConfigs);
    newMap.set(sidebetType, { ...config, chipsPlacedForCountdown: true });
    console.log(`[GameState] markSidebetChipsPlaced: ${sidebetType} - chips arrived, ready for countdown`);
    set({ mySidebetConfigs: newMap });
  },

  removeSidebetConfig: (sidebetType) => {
    console.log(`[GameState] removeSidebetConfig: ${sidebetType}`);
    const { mySidebetConfigs } = get();
    const newMap = new Map(mySidebetConfigs);
    newMap.delete(sidebetType);
    set({ mySidebetConfigs: newMap });
  },

  decrementSidebetConfigRounds: () => {
    const { mySidebetConfigs } = get();
    const newMap = new Map<string, { percentage: number; rounds: number; chipsPlacedForCountdown: boolean }>();

    for (const [sidebetType, config] of mySidebetConfigs) {
      // Only decrement if chips were placed this round (complete round = arrival + departure)
      if (!config.chipsPlacedForCountdown) {
        // Chips weren't placed under current config, keep config unchanged
        newMap.set(sidebetType, config);
        continue;
      }

      if (config.rounds === -1) {
        // Ongoing - keep as is, reset placement flag for next round
        newMap.set(sidebetType, { ...config, chipsPlacedForCountdown: false });
      } else if (config.rounds > 1) {
        // Countdown - decrement and reset placement flag
        newMap.set(sidebetType, { ...config, rounds: config.rounds - 1, chipsPlacedForCountdown: false });
      }
      // If rounds === 1, don't add to new map (expired)
    }

    console.log(`[GameState] decrementSidebetConfigRounds: ${mySidebetConfigs.size} -> ${newMap.size} configs`);
    set({ mySidebetConfigs: newMap });
  },

  decrementSidebetConfig: (sidebetType) => {
    const { mySidebetConfigs } = get();
    const config = mySidebetConfigs.get(sidebetType);

    if (!config) {
      console.warn(`[GameState] decrementSidebetConfig: ${sidebetType} not found`);
      return;
    }

    // Only decrement if chips were placed this round (complete round = arrival + departure)
    if (!config.chipsPlacedForCountdown) {
      console.log(`[GameState] decrementSidebetConfig: ${sidebetType} - chips not placed under current config, no decrement`);
      return;
    }

    const newMap = new Map(mySidebetConfigs);

    if (config.rounds === -1) {
      // Ongoing - keep as is, reset placement flag for next round
      newMap.set(sidebetType, { ...config, chipsPlacedForCountdown: false });
      console.log(`[GameState] decrementSidebetConfig: ${sidebetType} is ongoing, no decrement (reset flag)`);
    } else if (config.rounds > 1) {
      // Countdown - decrement and reset placement flag
      newMap.set(sidebetType, { ...config, rounds: config.rounds - 1, chipsPlacedForCountdown: false });
      console.log(`[GameState] decrementSidebetConfig: ${sidebetType} ${config.rounds} -> ${config.rounds - 1}`);
    } else {
      // rounds === 1 - this is the last round, remove after this
      newMap.delete(sidebetType);
      console.log(`[GameState] decrementSidebetConfig: ${sidebetType} expired (was last round)`);
    }

    set({ mySidebetConfigs: newMap });
  },

  clearSidebetConfigs: () => {
    set({ mySidebetConfigs: new Map() });
  },

  // ===========================================
  // SERVER RECOMMENDATION ACTIONS
  // ===========================================

  setServerRecommendation: (action, reason, isDeviation, seatPosition, handIndex) => {
    set({
      serverRecommendation: {
        action,
        reason,
        isDeviation,
        seatPosition,
        handIndex,
      },
    });
  },

  setServerInsuranceRecommendation: (action, reason, isDeviation, seatPosition) => {
    set({
      serverInsuranceRecommendation: {
        action,
        reason,
        isDeviation,
        seatPosition,
      },
    });
  },

  clearServerRecommendation: () => {
    set({ serverRecommendation: null });
  },

  clearServerInsuranceRecommendation: () => {
    set({ serverInsuranceRecommendation: null });
  },

  setInsuranceDecisionPending: (pending: boolean) => {
    set({ insuranceDecisionPending: pending });
  },
}));

// ===========================================
// SELECTORS
// ===========================================

export const selectPhase = (state: GameStateStore) => state.phase;
export const selectDealer = (state: GameStateStore) => state.dealer;
export const selectDealerPersona = (state: GameStateStore) => state.dealerPersona;
export const selectPlayers = (state: GameStateStore) => state.players;
export const selectCurrentTurnSeat = (state: GameStateStore) => state.currentTurnSeat;
export const selectMySeatPosition = (state: GameStateStore) => state.mySeatPosition;
export const selectIsMyTurn = (state: GameStateStore) =>
  state.mySeatPosition !== null && state.currentTurnSeat === state.mySeatPosition;
export const selectAvailableActions = (state: GameStateStore) => state.availableActions;
export const selectTurnSignal = (state: GameStateStore) => state.turnSignal;
export const selectInsuranceOffered = (state: GameStateStore) => state.insuranceOffered;
export const selectInsuranceDeadline = (state: GameStateStore) => state.insuranceDeadline;
export const selectInsuranceDecisionPending = (state: GameStateStore) => state.insuranceDecisionPending;
export const selectCardsDealt = (state: GameStateStore) => state.cardsDealt;
export const selectPenetration = (state: GameStateStore) => state.penetration;
export const selectReshufflePending = (state: GameStateStore) => state.reshufflePending;
export const selectConnectionWarnings = (state: GameStateStore) => state.connectionWarnings;

export function selectMyPlayerState(state: GameStateStore): PlayerGameState | null {
  if (state.mySeatPosition === null) return null;
  return state.players.get(state.mySeatPosition) ?? null;
}

export function selectPlayerAtSeat(seat: number) {
  return (state: GameStateStore) => state.players.get(seat) ?? null;
}

export function selectIsOpponentBetAnimating(seatPosition: number) {
  return (state: GameStateStore) => state.animatingOpponentBets.has(seatPosition);
}

// ===========================================
// HOOKS
// ===========================================

export function useGamePhase() {
  return useGameStateStore(selectPhase);
}

export function useDealer() {
  return useGameStateStore(selectDealer);
}

export function useDealerPersona() {
  return useGameStateStore(selectDealerPersona);
}

export function useIsMyTurn() {
  return useGameStateStore(selectIsMyTurn);
}

export function useMyPlayerState() {
  return useGameStateStore(selectMyPlayerState);
}

export function useAvailableActions() {
  return useGameStateStore(selectAvailableActions);
}

export function useTurnSignal() {
  return useGameStateStore(selectTurnSignal);
}

export function useInsuranceOffered() {
  return useGameStateStore(selectInsuranceOffered);
}

export function useInsuranceDeadline() {
  return useGameStateStore(selectInsuranceDeadline);
}

export function useInsuranceDecisionPending() {
  return useGameStateStore(selectInsuranceDecisionPending);
}

export function useCardsDealt() {
  return useGameStateStore(selectCardsDealt);
}

export function usePenetration() {
  return useGameStateStore(selectPenetration);
}

export function useReshufflePending() {
  return useGameStateStore(selectReshufflePending);
}

export function useBettingCountdown() {
  return useGameStateStore(state => ({
    deadline: state.bettingCountdownDeadline,
    active: state.bettingCountdownActive,
    playersNeedingBets: state.playersNeedingBets,
  }));
}

export function useInsuranceCountdown() {
  return useGameStateStore(state => ({
    deadline: state.insuranceCountdownDeadline,
    active: state.insuranceCountdownActive,
    seatPosition: state.insuranceCountdownSeat,
  }));
}

export function useTurnCountdown() {
  return useGameStateStore(state => ({
    deadline: state.turnCountdownDeadline,
    active: state.turnCountdownActive,
    seatPosition: state.turnCountdownSeat,
  }));
}

export default useGameStateStore;
